import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../services/api';
import {
  getRosterUpload,
  getRosterUploadOrganizations,
  getRosterUploadProspects,
  getRosterUploads,
} from '../../../services/admin-roster-uploads';
import type {
  RosterUploadDetail,
  RosterUploadListResponse,
  RosterUploadOrganization,
  RosterUploadProspect,
} from '../../../types/adminRosterUploads';
import { describeError, useAdminFetch, type AdminFetchState } from '../useAdminFetch';
import { ROSTER_UPLOADS_PAGE_SIZE, type RosterUploadsFilters } from './rosterUploadsNavigation';

/**
 * The upload list for a set of filters.
 *
 * Changing a filter or typing in search starts a new request before the last one has necessarily
 * come back. The abort covers the common case; the token covers a response that had already
 * resolved when the signal fired, so an earlier, slower answer never paints over a later one.
 *
 * The previous response stays in `data` while the next loads, so the summary tiles do not collapse
 * and reflow on every filter change. `loading` says it is stale.
 *
 * `error` likewise stays until the next request settles — cleared by a success, replaced by another
 * failure — rather than being dropped the moment a retry starts. The banner that shows it holds the
 * Try again button, and unmounting that while it has focus drops keyboard focus to the body.
 */
export const useRosterUploads = (filters: RosterUploadsFilters) => {
  const { organizationId, status, sourceType, search, page } = filters;
  const [data, setData] = useState<RosterUploadListResponse | null>(null);
  // When `data` arrived. Relative times ("5m ago") are measured from here rather than from a
  // `new Date()` in render, so they are stable across re-renders and testable.
  const [receivedAt, setReceivedAt] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestToken = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const token = requestToken.current + 1;
    requestToken.current = token;

    setLoading(true);

    getRosterUploads(
      {
        pageNumber: page,
        pageSize: ROSTER_UPLOADS_PAGE_SIZE,
        ...(organizationId ? { organizationId } : {}),
        ...(status ? { status } : {}),
        ...(sourceType ? { sourceType } : {}),
        ...(search ? { search } : {}),
      },
      controller.signal
    )
      .then((response) => {
        if (requestToken.current !== token) return;
        setData(response);
        setReceivedAt(new Date());
        setError(null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || requestToken.current !== token) return;
        console.error('Failed to load roster uploads:', err);
        setError(describeError(err, 'Could not load uploaded rosters. Please try again.'));
        setLoading(false);
      });

    return () => controller.abort();
  }, [organizationId, status, sourceType, search, page, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return { data, receivedAt, loading, error, reload };
};

/**
 * Organizations for the filter. A failure here is not fatal to the page — the list still works
 * unfiltered — so it is reported as a flag and the select explains itself.
 */
export const useRosterUploadOrganizations = () => {
  const [organizations, setOrganizations] = useState<RosterUploadOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    getRosterUploadOrganizations(controller.signal)
      .then((found) => {
        setOrganizations(found);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load roster upload organizations:', err);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { organizations, loading, failed };
};

export type RosterUploadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; upload: RosterUploadDetail };

/** One upload. A 404 is its own state, so "it does not exist" never reads as "try again". */
export const useRosterUpload = (id: string | undefined) => {
  const [state, setState] = useState<RosterUploadState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!id) {
      setState({ kind: 'not-found' });
      return;
    }

    const controller = new AbortController();
    setState({ kind: 'loading' });

    getRosterUpload(id, controller.signal)
      .then((upload) => {
        if (controller.signal.aborted) return;
        setState({ kind: 'loaded', upload });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.statusCode === 404) {
          setState({ kind: 'not-found' });
          return;
        }
        console.error('Failed to load roster upload:', err);
        setState({
          kind: 'error',
          message: describeError(err, 'Could not load this roster upload. Please try again.'),
        });
      });

    return () => controller.abort();
  }, [id, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return { state, reload };
};

export type RosterUploadProspectsState = AdminFetchState<RosterUploadProspect[]>;

const PROSPECTS_MESSAGES = {
  log: 'Failed to load roster upload prospects:',
  fallback: 'Could not load the prospects from this upload.',
};

/**
 * The prospects an upload produced or matched. Separate from the upload itself so a failure here
 * costs only its own section, not the whole page, and has its own retry.
 */
export const useRosterUploadProspects = (id: string) =>
  useAdminFetch(id, getRosterUploadProspects, PROSPECTS_MESSAGES);
